import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useActiveIntegrations } from '../../integration';

export enum ActionRequiredTab {
    NewManagements = 'new-managements',
    ReportedFindings = 'reported-findings',
    UrgentActiveNotices = 'urgent-active-notices',
    UrgentQuotes = 'urgent-quotes',
    OverdueInvoices = 'overdue-invoices'
}

const actionRequiredTabs = [
    ActionRequiredTab.NewManagements,
    ActionRequiredTab.ReportedFindings,
    ActionRequiredTab.UrgentActiveNotices,
    ActionRequiredTab.UrgentQuotes,
    ActionRequiredTab.OverdueInvoices,
];

const getAvailableActionRequiredTabs = (
    isIntegrated: boolean
) => actionRequiredTabs.filter((tab) => {
    if (tab === ActionRequiredTab.UrgentQuotes) {
        return false;
    }

    if (!isIntegrated && tab === ActionRequiredTab.NewManagements) {
        return false;
    }

    return true;
});

function getTabIndexFromName(
    tabName: ActionRequiredTab,
    tabItems: string[]
) {
    const idx = tabItems.indexOf(tabName);

    return idx !== -1 ? idx : 0;
}

function getNameFromTabIndex(
    tabIndex: number,
    tabItems: string[]
) {
    return tabItems[tabIndex] ?? ActionRequiredTab.NewManagements;
}

export function useActionRequiredTab() {
    const [params, setParams] = useSearchParams();
    const { isIntegrated } = useActiveIntegrations();

    const selectedTabIndex = useMemo(() => {
        const tabName = params.get('action-required-tab') as ActionRequiredTab | null;

        if (tabName === null) {
            return 0;
        }

        const tabItems = getAvailableActionRequiredTabs(isIntegrated);

        return getTabIndexFromName(tabName, tabItems);
    }, [params, isIntegrated]);

    const selectedTabName = useMemo(() => {
        const tabItems = getAvailableActionRequiredTabs(isIntegrated);

        return getNameFromTabIndex(selectedTabIndex, tabItems);
    }, [selectedTabIndex, isIntegrated]);

    const setTabByName = (tabName: ActionRequiredTab) => {
        setParams((old) => {
            old.set('action-required-tab', tabName);
            return old;
        });
    };

    const setTabByIndex = (tabIndex: number) => {
        setParams((old) => {
            const tabItems = getAvailableActionRequiredTabs(isIntegrated);

            old.set('action-required-tab', getNameFromTabIndex(tabIndex, tabItems));
            return old;
        });
    };

    return {
        selectedTabName,
        selectedTabIndex,
        setTabByName,
        setTabByIndex,
    };
}
